import { useState } from "react";
import { CopyIcon, CheckIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { Modal, PrimaryButton } from "../crm/Form";

/**
 * One-time temp-password reveal modal. Used for both Add Member and Reset
 * Password. The plaintext password is visible immediately and is never
 * persisted nor re-fetchable.
 */
export default function TempPasswordModal({
  open,
  onClose,
  email,
  tempPassword,
  variant = "create", // "create" | "reset"
  memberName,
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select-all on the input
      const el = document.querySelector(
        '[data-testid="temp-password-readonly"]'
      );
      if (el) el.select();
    }
  };

  const title =
    variant === "reset"
      ? `Password reset${memberName ? ` for ${memberName}` : ""}`
      : `Account created${memberName ? ` for ${memberName}` : ""}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={variant === "reset" ? "new temporary password" : "temporary password"}
      data-testid="temp-password-modal"
    >
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
          <WarningCircleIcon size={16} weight="fill" className="mt-0.5 flex-none" />
          <div>
            <div className="font-bold uppercase tracking-wide text-[10px] mb-1">
              Share this securely
            </div>
            This password will not be shown again. Copy it now and deliver it
            via a trusted channel (in-person, encrypted message, or SMS).
          </div>
        </div>

        {email && (
          <div className="text-xs">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              Email
            </div>
            <div
              data-testid="temp-password-email"
              className="mt-0.5 font-data text-sm text-slate-900 break-all"
            >
              {email}
            </div>
          </div>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
            Temporary password
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={tempPassword || ""}
              readOnly
              data-testid="temp-password-readonly"
              onClick={(e) => e.target.select()}
              className="flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm font-data text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
            />
            <button
              type="button"
              onClick={copy}
              data-testid="temp-password-copy"
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              {copied ? (
                <>
                  <CheckIcon size={14} className="text-emerald-600" /> Copied
                </>
              ) : (
                <>
                  <CopyIcon size={14} /> Copy
                </>
              )}
            </button>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <PrimaryButton type="button" onClick={onClose} data-testid="temp-password-done">
            Done
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}
